import React, { useCallback, useEffect } from 'react';
import { Grid, Box, Typography, useMediaQuery, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ReviewCard } from './Cards/ReviewCard';
import { ITaentReviewResponse, ITalentReview } from '../../types/global/helper';
import AddIcon from '@mui/icons-material/Add';
import RateReview from '@mui/icons-material/RateReview';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { getJWtDetails, useDispatchPromise } from '../../utils/AppUtils';
import { useThemeContext } from '../../hook/useThemeManager';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { currentTalentReviewsSelector } from '../../selectors/selectors';
import { getTalentReviews } from '../../redux/talentProfile/action';
import { Spinner } from '@chakra-ui/react';
import { Pagination } from '../BuzzBridge/components/Pagination';
import { usePagination } from '../../hook/usePagination';

export interface IReviews {
    reviews?: ITalentReview[];
    profileUserId?: string;
    onAddReview?: () => void;
    onEditReview?: (review: ITalentReview) => void;
    onDeleteReview?: (review: ITalentReview) => void;
    onLikeReview?: (review: ITalentReview) => void;
    onDislikeReview?: (review: ITalentReview) => void;
    onDeleteLikeReview?: (likeId: string, review: ITalentReview) => void;
}

const ROW = 6;

export const Reviews: React.FC<IReviews> = ({
    profileUserId,
    onAddReview,
    onEditReview,
    onDeleteReview,
    onLikeReview,
    onDislikeReview,
    onDeleteLikeReview,
}) => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const { themeStyle } = useThemeContext();
    const { loading, reviews } = useSelector(currentTalentReviewsSelector);
    const dispatch = useDispatchPromise();
    const { user_id } = getJWtDetails();
    const currentUserId = user_id;
    const canAddReview = profileUserId && currentUserId && profileUserId !== currentUserId;
    const isViewingOwnProfile = Boolean(profileUserId && currentUserId && profileUserId === currentUserId);
    const { handlePageChange, pagination, setPagination } = usePagination(1, ROW);
    const getUserReviews = useCallback(() => {
        if (profileUserId) {
            dispatch(
                getTalentReviews({
                    talentId: profileUserId,
                    limit: pagination.rows || ROW,
                    page: pagination.page || 1,
                }),
            ).then(response => {
                const load: ITaentReviewResponse = response as ITaentReviewResponse;
                const { currentPage, total, totalPages } = load;
                setPagination({
                    page: currentPage,
                    rows: ROW,
                    totalCount: total,
                    totalPages: totalPages,
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileUserId, pagination.page, pagination.rows]);

    useEffect(() => {
        getUserReviews();
    }, [getUserReviews]);

    return (
        <Grid
            container
            spacing={isMobile ? 2 : 3}
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                px: { xs: 2, sm: 3 },
                py: 2,
            }}>
            <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].highlight,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                            }}>
                            Feedback &amp; Reputation
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 34, md: 52 },
                            lineHeight: 1.08,
                            letterSpacing: '-0.02em',
                            color: theme[themeStyle].primary_white,
                            mb: 1.5,
                        }}>
                        Talent <span style={{ color: theme[themeStyle].highlight }}>Reviews</span>
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.62)',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 16,
                            maxWidth: 760,
                            lineHeight: 1.55,
                        }}>
                        Verified feedback from directors, producers, and crew members across the global production
                        network.
                    </Typography>
                </Box>
            </Grid>
            {canAddReview && !loading && !isEmpty(reviews) && (
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={onAddReview}
                            sx={{
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                borderRadius: '999px',
                                px: { xs: 2.25, sm: 2.75 },
                                py: 1.1,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                            }}>
                            Add Review
                        </Button>
                    </Box>
                </Grid>
            )}
            {loading && (
                <Box sx={{ width: '100%', justifyItems: 'center', alignItems: 'center' }}>
                    <Spinner height={10} w={10} sx={{ display: 'flex' }} color={theme[themeStyle].tag_white} />
                </Box>
            )}
            {!!reviews &&
                !loading &&
                !isEmpty(reviews) &&
                reviews.map((review, index) => {
                    const canEditDelete = review.user?._id === currentUserId;
                    return (
                        <Grid item xs={12} key={index}>
                            <ReviewCard
                                review={review}
                                canEditDelete={canEditDelete}
                                onEdit={onEditReview ? () => onEditReview(review) : undefined}
                                onDelete={onDeleteReview ? () => onDeleteReview(review) : undefined}
                                onLike={onLikeReview ? () => onLikeReview(review) : undefined}
                                onDislike={onDislikeReview ? () => onDislikeReview(review) : undefined}
                                onDeleteLike={
                                    onDeleteLikeReview ? likeId => onDeleteLikeReview(likeId, review) : undefined
                                }
                            />
                        </Grid>
                    );
                })}
            {!loading && isEmpty(reviews) && (
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            py: { xs: 2, sm: 3 },
                            px: { xs: 1, sm: 0 },
                        }}>
                        <Box sx={{ position: 'relative', width: '100%', maxWidth: 672, mx: 'auto' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -96,
                                    left: -96,
                                    width: 256,
                                    height: 256,
                                    borderRadius: '50%',
                                    bgcolor: alpha(theme[themeStyle].highlight, 0.12),
                                    filter: 'blur(120px)',
                                    pointerEvents: 'none',
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -96,
                                    right: -96,
                                    width: 256,
                                    height: 256,
                                    borderRadius: '50%',
                                    bgcolor: alpha(theme[themeStyle].highlight, 0.06),
                                    filter: 'blur(100px)',
                                    pointerEvents: 'none',
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'relative',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    border: `1px solid ${alpha(theme[themeStyle].tag_white, 0.1)}`,
                                    bgcolor: alpha(theme[themeStyle].true_black, 0.55),
                                    transition: 'border-color 0.5s ease, box-shadow 0.5s ease',
                                    '&:hover': {
                                        borderColor: alpha(theme[themeStyle].tag_white, 0.22),
                                        boxShadow: `0 0 72px ${alpha(theme[themeStyle].highlight, 0.1)}`,
                                    },
                                }}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        opacity: 0.1,
                                        pointerEvents: 'none',
                                        backgroundImage:
                                            'url(https://www.transparenttextures.com/patterns/carbon-fibre.png)',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'relative',
                                        zIndex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        px: { xs: 4, md: 8 },
                                        py: { xs: 8, md: 10 },
                                    }}>
                                    <Box sx={{ mb: 4, position: 'relative' }}>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                bgcolor: alpha(theme[themeStyle].highlight, 0.2),
                                                filter: 'blur(24px)',
                                                borderRadius: '50%',
                                                transform: 'scale(1.5)',
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: 80,
                                                height: 80,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: '#262626',
                                                border: `1px solid ${alpha(theme[themeStyle].highlight, 0.22)}`,
                                            }}>
                                            <RateReview
                                                sx={{
                                                    fontSize: 48,
                                                    color: theme[themeStyle].highlight,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Typography
                                        component="h2"
                                        sx={{
                                            fontFamily: FONT_ENUM.BOLD_HEADER,
                                            fontWeight: 800,
                                            fontSize: { xs: '1.875rem', md: '2.25rem' },
                                            letterSpacing: '-0.03em',
                                            lineHeight: 1.15,
                                            color: theme[themeStyle].primary_white,
                                            mb: 2,
                                        }}>
                                        {isViewingOwnProfile ? (
                                            <>
                                                No reviews{' '}
                                                <Box component="span" sx={{ color: theme[themeStyle].highlight }}>
                                                    yet
                                                </Box>
                                                .
                                            </>
                                        ) : (
                                            <>
                                                No reviews available{' '}
                                                <Box component="span" sx={{ color: theme[themeStyle].highlight }}>
                                                    yet
                                                </Box>
                                                .
                                            </>
                                        )}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: 'rgba(255,255,255,0.65)',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 18,
                                            lineHeight: 1.65,
                                            maxWidth: 448,
                                            mb: canAddReview && onAddReview ? 4 : 0,
                                            fontWeight: 500,
                                        }}>
                                        {isViewingOwnProfile
                                            ? 'Start collaborating on projects and request feedback from directors and crew to build your reputation.'
                                            : "Be the first to share feedback on this creator's work."}
                                    </Typography>
                                    {canAddReview && onAddReview ? (
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={onAddReview}
                                            sx={{
                                                backgroundColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].black,
                                                borderRadius: '999px',
                                                px: { xs: 2.25, sm: 2.75 },
                                                py: 1.1,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 13,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                                alignSelf: 'center',
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].primary_orange,
                                                    opacity: 0.9,
                                                },
                                            }}>
                                            Add Review
                                        </Button>
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            )}
            {!isEmpty(reviews) && (
                <Box sx={{ width: '100%', m: 3 }}>
                    <Pagination
                        currentPage={pagination.page}
                        totalPages={pagination.totalPages || 0}
                        onPageChange={handlePageChange}
                    />
                </Box>
            )}
        </Grid>
    );
};
